import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useClientLocations from '@ubeya/shared/hooks/account/useClientLocations';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import useModules from '@ubeya/shared/hooks/account/useModules';
import useSpecificLocation from '@ubeya/shared/hooks/account/useSpecificLocation';
import useClientSpecificLocation from '@ubeya/shared/hooks/account/useClientSpecificLocation';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { SORT_BY_STRING } from '@ubeya/shared/utils/sorting';

const SHOW_LOCATION_STATUS = {
  ALL: -1,
  LESS: -2
};

const useClientAndLocationSelectionLogic = ({
  defaultProjectGeneralComment = '',
  locationErrors = {},
  setLocationId,
  projectLocationIdValue,
  clientBranchIdValue,
  setClientBranchId,
  publicComments,
  onChangePublicComments,
  locationIdValue,
  isEdit = false,
  showSubLocations = false,
  useOnlyMainLocation = false
}) => {
  const { mappedClientBranches } = useCRM();
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const theme = useTheme();
  const { modules } = useModules();

  const [clientId, setClientId] = useState(null);
  const [isAddingLocation, setIsAddingLocation] = useState(false);

  const [addedLocationsOptions, setAddedLocationsOptions] = useState({});
  const [showlocationStatus, setShowlocationStatus] = useState(null);

  const [clientLoc, setClientLoc] = useState();

  const { locations, addLocation, mappedLocations: mappedAccountLocations, refetch } = useLocations();

  const labelSorter = SORT_BY_STRING('label');

  const { subLocations, mappedSubLocations, isLoading, addSubLocation } = useSpecificLocation({
    locationId: projectLocationIdValue
  });

  const {
    data: clientLocation,
    subLocations: clientSubLocations,
    isLoading: isLoadingClientLocation
  } = useClientSpecificLocation({
    locationId: clientLoc,
    clientBranchId: clientBranchIdValue,
    clientId,
    enable: showSubLocations
  });

  const { mappedClientBranchesLocations } = useClientLocations({
    accountId,
    clientId
  });

  useEffect(() => {
    clientBranchIdValue && setClientId(mappedClientBranches?.[clientBranchIdValue]?.id);
  }, [clientBranchIdValue, mappedClientBranches]);

  const showAllLocationsCase = useMemo(() => isEdit || showlocationStatus === SHOW_LOCATION_STATUS.ALL, [
    isEdit,
    showlocationStatus
  ]);

  const handleClientGeneralComments = useCallback(
    (clientBranchId) => {
      /* if the user added a general comment don't change it upon changing client */
      if (publicComments && publicComments !== defaultProjectGeneralComment) {
        return;
      }
      const clientDefaultComment = mappedClientBranches?.[clientBranchId]?.defaultComment;

      onChangePublicComments?.(clientDefaultComment || defaultProjectGeneralComment);
    },
    [defaultProjectGeneralComment, mappedClientBranches, onChangePublicComments, publicComments]
  );

  const getOptionSpecificStyle = useCallback(
    (option) => {
      switch (option.value) {
        case null:
          return { borderTop: `1px solid ${theme.colors.grey2}` };

        case SHOW_LOCATION_STATUS.ALL:
        case SHOW_LOCATION_STATUS.LESS:
          return {
            textAlign: 'center',
            color: theme.colors.primary
          };

        default:
          return {};
      }
    },
    [theme.colors.grey2, theme.colors.primary]
  );

  const onChangeClientBranchId = useCallback(
    (clientBranchId) => {
      setClientBranchId(clientBranchId);
      setClientId(mappedClientBranches?.[clientBranchId]?.id);
      setLocationId(null);
      handleClientGeneralComments(clientBranchId);
    },
    [setClientBranchId, mappedClientBranches, setLocationId, handleClientGeneralComments]
  );

  const formatSubLocation = (subLocationName, parentLocationName) => `${subLocationName} (${parentLocationName})`;

  const customSubLocationsOptions = useMemo(() => {
    if (!showSubLocations) {
      return [];
    }
    const supplierSubLocationsInClient = new Set();

    const subLocationsOptions = (subLocations || []).map((location) => ({
      value: location.id,
      label: formatSubLocation(location.name, mappedAccountLocations[projectLocationIdValue].name),
      lat: location.lat,
      lng: location.lng,
      isSubLocation: true
    }));

    const clientBranchSubLocations =
      clientBranchIdValue &&
      (clientSubLocations || []).map((clientSubLocation) => {
        /* if same sub location exists in current account ,take the location id from current.
        else, save as is */

        const subLocationExistsInSupplier = (subLocations || []).find(
          ({ lat, name, lng }) =>
            clientSubLocation.lat === lat &&
            clientSubLocation.lng === lng &&
            (clientSubLocation.name === name ||
              name === `${clientSubLocation.name} (${mappedClientBranches[clientBranchIdValue]?.name})`)
        );

        const selectedSubLocation = subLocationExistsInSupplier || clientSubLocation;

        if (subLocationExistsInSupplier) {
          supplierSubLocationsInClient.add(subLocationExistsInSupplier.id);
        }

        return {
          value: selectedSubLocation.id,
          label: formatSubLocation(selectedSubLocation.name, clientLocation?.name),
          lat: selectedSubLocation.lat,
          lng: selectedSubLocation.lng,
          clientSubLocationId: clientSubLocation.id,
          isSubLocation: true
        };
      });

    if (clientBranchSubLocations) {
      const supplierSubLocationsNotInClient = (subLocationsOptions || []).filter(
        ({ value }) => !supplierSubLocationsInClient.has(value)
      );

      /* IF:
          - location id value is selected
          - not part of the client locations
          - showAllLocationsCase if off, meaning we only see the client location

        -> prioritize this location into the 'show less' locations options
      */

      if (locationIdValue && !clientBranchSubLocations.find(({ value }) => value === locationIdValue)) {
        const currentSubLocationSelectedData = mappedSubLocations?.[locationIdValue];

        if (currentSubLocationSelectedData) {
          supplierSubLocationsNotInClient.push({
            value: currentSubLocationSelectedData.id,
            label: formatSubLocation(currentSubLocationSelectedData.name, clientLocation?.name),
            lat: currentSubLocationSelectedData.lat,
            lng: currentSubLocationSelectedData.lng,
            isSubLocation: true
          });
        }
      }

      return clientBranchSubLocations.concat(supplierSubLocationsNotInClient).sort(labelSorter);
    }

    return subLocationsOptions.sort(labelSorter);
  }, [
    showSubLocations,
    subLocations,
    clientBranchIdValue,
    clientSubLocations,
    mappedAccountLocations,
    projectLocationIdValue,
    clientLocation?.name,
    mappedClientBranches,
    locationIdValue,
    labelSorter,
    mappedSubLocations
  ]);

  const customLocationsOptions = useMemo(() => {
    const locationsOptions = (locations || [])
      .filter(({ isSubLocation }) => !isSubLocation)
      .map((location) => ({
        value: location.id,
        label: location.name,
        lat: location.lat,
        lng: location.lng
      }));

    if (useOnlyMainLocation) {
      /* if flag useOnlyMainLocation show only selected project location + it's corresponding sub locations*/

      const mainLocationOption = locationsOptions.find(({ value }) => value === projectLocationIdValue);

      /* mainLocationOption can be undefined in case the location is deleted (handles in the location select field) */
      return [...(mainLocationOption ? [mainLocationOption] : []), ...(customSubLocationsOptions || [])];
    }
    const supplierLocationsInClient = new Set();

    const clientBranchLocations =
      clientBranchIdValue &&
      (mappedClientBranchesLocations?.[clientBranchIdValue] || [])
        .map((clientLocationItem) => {
          /* if same location exists in current account ,take the location id from current.
        else, save as is */

          const locationExistsInSupplier = (locations || [])
            .filter(({ isSubLocation }) => !isSubLocation)
            .find(
              ({ lat, name, lng }) =>
                clientLocationItem.lat === lat &&
                clientLocationItem.lng === lng &&
                (clientLocationItem.name === name ||
                  name === `${clientLocationItem.name} (${mappedClientBranches[clientBranchIdValue]?.name})`)
            );

          const selectedLocation = locationExistsInSupplier || clientLocationItem;

          if (locationExistsInSupplier) {
            supplierLocationsInClient.add(locationExistsInSupplier.id);
          }

          return {
            value: selectedLocation.id,
            label: selectedLocation.name,
            lat: selectedLocation.lat,
            lng: selectedLocation.lng,
            clientLocationId: clientLocationItem.id
          };
        })
        .concat(addedLocationsOptions?.[clientBranchIdValue] || []);

    if (clientBranchLocations) {
      const supplierLocationsNotInClient = showAllLocationsCase
        ? (locationsOptions || []).filter(({ value }) => !supplierLocationsInClient.has(value))
        : [];

      /* IF:
          - location id value is selected
          - not part of the client locations
          - showAllLocationsCase if off, meaning we only see the client location

        -> prioritize this location into the 'show less' locations options
      */

      if (
        locationIdValue &&
        !clientBranchLocations.find(({ value }) => value === locationIdValue) &&
        !showAllLocationsCase
      ) {
        const currentLocationSelectedData = mappedAccountLocations?.[locationIdValue];

        if (currentLocationSelectedData) {
          supplierLocationsNotInClient.push({
            value: currentLocationSelectedData.id,
            label: currentLocationSelectedData.name,
            lat: currentLocationSelectedData.lat,
            lng: currentLocationSelectedData.lng
          });
        }
      }

      return (customSubLocationsOptions || [])
        .concat(clientBranchLocations)
        .concat(supplierLocationsNotInClient)
        .concat([
          showAllLocationsCase
            ? {
                value: SHOW_LOCATION_STATUS.LESS,
                label: t('showLess')
              }
            : { value: SHOW_LOCATION_STATUS.ALL, label: t('showMore') }
        ]);
    }

    return (customSubLocationsOptions || []).concat(locationsOptions);
  }, [
    locations,
    clientBranchIdValue,
    mappedClientBranchesLocations,
    addedLocationsOptions,
    useOnlyMainLocation,
    projectLocationIdValue,
    customSubLocationsOptions,
    mappedClientBranches,
    showAllLocationsCase,
    locationIdValue,
    t,
    mappedAccountLocations
  ]);

  useEffect(() => {
    // get extra data beside id of the selcetd option
    const origin = customLocationsOptions?.find(({ value: locationId }) => projectLocationIdValue === locationId);

    if (origin?.clientLocationId) {
      setClientLoc(origin.clientLocationId);
    }
    // eslint-disable-next-line no-console
  }, [customLocationsOptions, projectLocationIdValue]);

  const onChangeLocation = useCallback(
    async (id, isAdded = false, addedLocationData) => {
      if ([SHOW_LOCATION_STATUS.ALL, SHOW_LOCATION_STATUS.LESS].includes(id)) {
        return setShowlocationStatus(id);
      }

      if (id == null) {
        return setLocationId(null);
      }

      const locationExistsInCurrentBranch = !!locations
        ?.filter(({ isSubLocation }) => !isSubLocation)
        .find(({ id: locationId }) => id === locationId);

      if (locationExistsInCurrentBranch || isAdded) {
        if (isAdded && addedLocationData) {
          setAddedLocationsOptions((prev) => {
            const res = {
              ...prev,
              [clientBranchIdValue]: [
                ...(prev[clientBranchIdValue] || []),
                {
                  value: addedLocationData.id,
                  label: `${addedLocationData.name}`,
                  lat: addedLocationData.lat,
                  lng: addedLocationData.lng
                }
              ]
            };
            return res;
          });
        }

        return setLocationId(id);
      }

      const locationIsSubLocation = !!subLocations?.find(({ id: locationId }) => id === locationId);

      //is sub location
      if (locationIsSubLocation) {
        return setLocationId(id);
      }

      const newLocationData = mappedClientBranchesLocations?.[clientBranchIdValue]?.find(
        ({ id: clientLocationId }) => clientLocationId === id
      );

      const newSubLocationData = clientSubLocations?.find(({ id: clientLocationId }) => clientLocationId === id);

      if (!newLocationData && !newSubLocationData) {
        return;
      }

      if (newSubLocationData) {
        setIsAddingLocation(true);
        await addSubLocation({ locationId: newSubLocationData.id, clientBranchId: clientBranchIdValue });

        await refetch();
        setIsAddingLocation(false);
        setLocationId(newSubLocationData.id);
      }

      if (newLocationData) {
        const { name, address, lat, lng } = newLocationData;

        const nameAlreadyExists = (locations || []).some(
          ({ name: locationName }) =>
            locationName?.replace(/\s+$/, '')?.toLowerCase() === name?.replace(/\s+$/, '')?.toLowerCase()
        );

        const newLocationName = `${name}${
          nameAlreadyExists ? ` (${mappedClientBranches[clientBranchIdValue]?.name})` : ''
        }`;

        setIsAddingLocation(true);
        const result = await addLocation({ name: newLocationName, address, lat, lng });

        setIsAddingLocation(false);
        setLocationId(result?.data?.id);
      }
    },
    [
      locations,
      subLocations,
      mappedClientBranchesLocations,
      clientBranchIdValue,
      clientSubLocations,
      setLocationId,
      addSubLocation,
      refetch,
      mappedClientBranches,
      addLocation
    ]
  );

  const checkCloseUponLocationOptionSelectFunc = useCallback(
    (id) => ![SHOW_LOCATION_STATUS.ALL, SHOW_LOCATION_STATUS.LESS].includes(id),
    []
  );

  return {
    showClientField: modules.crm,
    onAddClient: setClientBranchId,
    onChangeClientBranchId: onChangeClientBranchId,
    isLoadingLocationField: isLoading || isAddingLocation || isLoadingClientLocation,
    customLocationsOptions,
    onChangeLocation,
    locationErrors,
    getLocationOptionSpecificStyle: getOptionSpecificStyle,
    checkCloseUponLocationOptionSelectFunc
  };
};

export default useClientAndLocationSelectionLogic;
